/* Document generator for admin-created invoices and quotes
   Created: Generates HTML for PDF rendering, supports both invoice (FACTUUR) and quote (OFFERTE) documents
   Uses the same visual style as the existing order confirmation invoice
   Updated: PDF totals now show a per-rate BTW breakdown (0% / 9% / 21%) using
   each line's btw_percentage instead of assuming 9%.
   Updated: Invoices can show the order's delivery day or pickup moment (delivery_info). */

import { COMPANY_INFO } from './company-info';
import { calculateBTWBreakdown } from './btw-calculator';
import type { OrderDeliveryInfo } from './delivery-date';

export interface DocumentItem {
  description: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  btw_percentage: number;
}

export interface DocumentData {
  document_number: string;
  document_type: 'invoice' | 'quote';
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_address: string;
  customer_city: string;
  customer_postal_code: string;
  customer_notes?: string;
  subtotal: number;
  delivery_fee: number;
  discount_amount: number;
  total: number;
  valid_until?: string;
  created_at: string;
  delivery_info?: OrderDeliveryInfo;
}

export async function createDocumentElement(
  doc: DocumentData,
  items: DocumentItem[]
): Promise<HTMLElement> {
  const docDate = new Date(doc.created_at).toLocaleDateString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const isInvoice = doc.document_type === 'invoice';
  const title = isInvoice ? 'FACTUUR' : 'OFFERTE';
  const numberLabel = isInvoice ? 'Factuurnummer' : 'Offertenummer';
  const dateLabel = isInvoice ? 'Factuurdatum' : 'Offertedatum';

  let logoDataUrl = '';
  try {
    const response = await fetch('/assets/logos/buitnplanten-logo-green.webp');
    const blob = await response.blob();
    const reader = new FileReader();
    logoDataUrl = await new Promise((resolve) => {
      reader.onload = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error loading logo:', error);
  }

  const itemsHTML = items
    .map(
      (item, index) => `
    <tr style="background: ${index % 2 === 0 ? '#f9f9f9' : 'white'};">
      <td style="padding: 8px; font-size: 10px; border: 1px solid #ddd;">${item.description}</td>
      <td style="padding: 8px; text-align: center; font-size: 10px; border: 1px solid #ddd;">${item.quantity}</td>
      <td style="padding: 8px; text-align: right; font-size: 10px; border: 1px solid #ddd;">&euro;${item.unit_price.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
      <td style="padding: 8px; text-align: center; font-size: 10px; border: 1px solid #ddd;">${item.btw_percentage}%</td>
      <td style="padding: 8px; text-align: right; font-size: 10px; border: 1px solid #ddd;">&euro;${item.total_price.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
    </tr>
  `
    )
    .join('');

  const breakdown = calculateBTWBreakdown(
    items.map((i) => ({ totalInclBTW: i.total_price, btwPercentage: i.btw_percentage }))
  );
  const btwRowsHTML = breakdown.entries
    .map(
      (entry) => `
        <div style="display: flex; justify-content: space-between; margin-bottom: 5px; font-size: 11px;">
          <span>BTW (${entry.percentage}%):</span>
          <span>&euro;${entry.btwAmount.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
      `
    )
    .join('');

  const el = document.createElement('div');
  el.style.cssText = `
    position: absolute;
    top: -9999px;
    left: -9999px;
    width: 210mm;
    min-height: 297mm;
    background: white;
    padding: 20mm;
    font-family: Arial, sans-serif;
    font-size: 12px;
    line-height: 1.4;
  `;

  const validUntilSection =
    !isInvoice && doc.valid_until
      ? `<p style="margin: 5px 0 0 0; font-size: 10px;">Geldig tot: ${new Date(doc.valid_until).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })}</p>`
      : '';

  const paymentSection = isInvoice
    ? `
    <div style="background: #fff9e6; border: 2px solid #ffc107; padding: 20px; border-radius: 5px; margin-bottom: 30px;">
      <h3 style="margin: 0 0 10px 0; color: #0F4D3D; font-size: 12px;">Betaalinformatie</h3>
      <div style="font-size: 10px; color: #666;">
        <p style="margin: 0 0 10px 0;">Gelieve het totaalbedrag over te maken naar:</p>
        <p style="margin: 0;"><strong>Rekeningnummer:</strong> ${COMPANY_INFO.bankAccount}</p>
        <p style="margin: 2px 0 0 0;"><strong>Ten name van:</strong> ${COMPANY_INFO.bankAccountName}</p>
        <p style="margin: 2px 0 0 0;"><strong>Bank:</strong> ${COMPANY_INFO.bankName}</p>
        <p style="margin: 10px 0 0 0;"><strong>Onder vermelding van:</strong> ${doc.document_number}</p>
      </div>
    </div>
  `
    : `
    <div style="background: #e8f5e9; border: 2px solid #0F4D3D; padding: 20px; border-radius: 5px; margin-bottom: 30px;">
      <h3 style="margin: 0 0 10px 0; color: #0F4D3D; font-size: 12px;">Offerte informatie</h3>
      <div style="font-size: 10px; color: #666;">
        <p style="margin: 0;">Deze offerte is vrijblijvend en onder voorbehoud van beschikbaarheid.</p>
        ${doc.valid_until ? `<p style="margin: 5px 0 0 0;"><strong>Geldig tot:</strong> ${new Date(doc.valid_until).toLocaleDateString('nl-NL', { day: '2-digit', month: 'long', year: 'numeric' })}</p>` : ''}
        <p style="margin: 10px 0 0 0;">Neem contact met ons op om deze offerte om te zetten naar een bestelling.</p>
      </div>
    </div>
  `;

  const footerText = isInvoice
    ? 'Bedankt voor de bestelling!'
    : 'Bedankt voor uw interesse in Buitn Planten!';

  el.innerHTML = `
    <div style="display: flex; justify-content: space-between; margin-bottom: 30px;">
      <div>
        ${logoDataUrl ? `<img src="${logoDataUrl}" alt="Buitn Planten Logo" style="height: 48px; margin-bottom: 12px; display: block;" crossorigin="anonymous" />` : '<div style="height: 48px; margin-bottom: 12px; background: #0F4D3D; color: white; padding: 10px; font-weight: bold; text-align: center; border-radius: 4px;">BUITN PLANTEN</div>'}
        <div style="margin-top: 10px; font-size: 10px; color: #666;">
          <p style="margin: 0;">${COMPANY_INFO.name}</p>
          <p style="margin: 10px 0 0 0;">${COMPANY_INFO.address}</p>
          <p style="margin: 0;">${COMPANY_INFO.postalCode} ${COMPANY_INFO.city}</p>
          <p style="margin: 10px 0 0 0;">KvK: ${COMPANY_INFO.kvk}</p>
          <p style="margin: 0;">BTW: ${COMPANY_INFO.btw}</p>
          <p style="margin: 10px 0 0 0;">${COMPANY_INFO.email}</p>
          <p style="margin: 0;">${COMPANY_INFO.website}</p>
        </div>
      </div>
      <div style="text-align: right;">
        <h1 style="color: #0F4D3D; font-size: 24px; margin: 0;">${title}</h1>
        <div style="margin-top: 10px; font-size: 10px;">
          <p style="margin: 0;"><strong>${numberLabel}: ${doc.document_number}</strong></p>
          <p style="margin: 5px 0 0 0;">${dateLabel}: ${docDate}</p>
          ${validUntilSection}
        </div>
      </div>
    </div>

    <div style="margin-bottom: 30px;">
      <div style="background: #f5f5f5; padding: 15px; border-radius: 5px;">
        <p style="margin: 0; font-weight: bold; font-size: 12px;">${doc.customer_name}</p>
        ${doc.customer_address ? `<p style="margin: 5px 0 0 0; font-size: 10px; color: #666;">${doc.customer_address}</p>` : ''}
        ${doc.customer_postal_code || doc.customer_city ? `<p style="margin: 2px 0 0 0; font-size: 10px; color: #666;">${doc.customer_postal_code} ${doc.customer_city}</p>` : ''}
        <p style="margin: 5px 0 0 0; font-size: 10px; color: #666;">${doc.customer_email}</p>
        ${doc.customer_phone ? `<p style="margin: 2px 0 0 0; font-size: 10px; color: #666;">${doc.customer_phone}</p>` : ''}
      </div>
    </div>

    ${isInvoice && doc.delivery_info ? `
    <div style="margin-bottom: 20px;">
      <div style="background: #f5f5f5; padding: 12px; border-radius: 5px; border-left: 4px solid #0F4D3D;">
        <p style="margin: 0; font-size: 11px; font-weight: bold; color: #0F4D3D;">${doc.delivery_info.label}</p>
        <p style="margin: 5px 0 0 0; font-size: 11px; color: #333;">${doc.delivery_info.value}</p>
        ${doc.delivery_info.note ? `<p style="margin: 4px 0 0 0; font-size: 10px; color: #666; font-style: italic;">${doc.delivery_info.note}</p>` : ''}
      </div>
    </div>
    ` : ''}

    ${doc.customer_notes ? `
    <div style="margin-bottom: 20px;">
      <div style="background: #e8f5e9; padding: 12px; border-radius: 5px; border-left: 4px solid #0F4D3D;">
        <p style="margin: 0; font-size: 11px; font-weight: bold; color: #0F4D3D;">Opmerkingen</p>
        <p style="margin: 5px 0 0 0; font-size: 10px; color: #666;">${doc.customer_notes}</p>
      </div>
    </div>
    ` : ''}

    <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px;">
      <thead>
        <tr style="background: #0F4D3D; color: white;">
          <th style="padding: 8px; text-align: left; font-size: 10px;">Omschrijving</th>
          <th style="padding: 8px; text-align: center; font-size: 10px;">Aantal</th>
          <th style="padding: 8px; text-align: right; font-size: 10px;">Prijs per stuk</th>
          <th style="padding: 8px; text-align: center; font-size: 10px;">BTW</th>
          <th style="padding: 8px; text-align: right; font-size: 10px;">Totaal</th>
        </tr>
      </thead>
      <tbody>
        ${itemsHTML}
      </tbody>
    </table>

    <div style="display: flex; justify-content: flex-end; margin-bottom: 30px;">
      <div style="width: 280px; background: #f5f5f5; padding: 15px; border-radius: 5px;">
        <div style="display: flex; justify-content: space-between; margin-bottom: 5px; font-size: 11px;">
          <span>Subtotaal excl. BTW:</span>
          <span>&euro;${breakdown.totalExclBTW.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
        ${btwRowsHTML}
        <div style="display: flex; justify-content: space-between; margin-bottom: 5px; font-size: 11px;">
          <span>Subtotaal incl. BTW:</span>
          <span>&euro;${doc.subtotal.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
        ${doc.delivery_fee > 0 ? `
        <div style="display: flex; justify-content: space-between; margin-bottom: 5px; font-size: 11px;">
          <span>Bezorgkosten:</span>
          <span>&euro;${doc.delivery_fee.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
        ` : ''}
        ${doc.discount_amount > 0 ? `
        <div style="display: flex; justify-content: space-between; margin-bottom: 5px; font-size: 11px; color: #16a34a;">
          <span>Korting:</span>
          <span>- &euro;${doc.discount_amount.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
        ` : ''}
        <div style="border-top: 2px solid #0F4D3D; padding-top: 10px; margin-top: 5px;">
          <div style="display: flex; justify-content: space-between; font-weight: bold; font-size: 14px;">
            <span>Totaal incl. BTW:</span>
            <span>&euro;${doc.total.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>
      </div>
    </div>

    ${paymentSection}

    <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; font-size: 10px; color: #666;">
      <p style="margin: 0;">${footerText}</p>
      <p style="margin: 5px 0 0 0;">${COMPANY_INFO.name} | ${COMPANY_INFO.email} | ${COMPANY_INFO.website}</p>
    </div>
  `;

  document.body.appendChild(el);
  return el;
}

export async function generateDocumentPDFBlob(
  element: HTMLElement
): Promise<string> {
  const html2canvas = (await import('html2canvas')).default;
  const jsPDF = (await import('jspdf')).default;

  await new Promise((resolve) => setTimeout(resolve, 500));

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    allowTaint: false,
    backgroundColor: '#ffffff',
    logging: false,
    windowWidth: 794,
    windowHeight: 1123,
  });

  const imgData = canvas.toDataURL('image/jpeg', 0.95);
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const imgWidth = canvas.width;
  const imgHeight = canvas.height;
  const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
  const imgX = (pdfWidth - imgWidth * ratio) / 2;

  pdf.addImage(
    imgData,
    'JPEG',
    imgX,
    0,
    imgWidth * ratio,
    imgHeight * ratio,
    undefined,
    'FAST'
  );

  const pdfOutput = pdf.output('datauristring');
  return pdfOutput.split(',')[1];
}

export function downloadDocumentPDF(base64: string, filename: string): void {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: 'application/pdf' });

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}
